import { makeStyles, Typography as MuiTypography } from '@material-ui/core';
import React, { FunctionComponent } from 'react';
import { useTranslate } from 'react-admin';
import { HistoryItemsChildsUpdatedProps } from '../../types';
import {
  History as HistoryType,
  HistoryDetails,
} from '../../../../../config/types/history';
import { isEmpty } from 'lodash';
import RangeField from '../HistoryFields/RangeField';
import TextField from '../HistoryFields/TextField';
import DateField from '../HistoryFields/DateField';
import TagField from '../HistoryFields/TagField';
import ArrayField from '../HistoryFields/ArrayField';
import WysiwygField from '../HistoryFields/WysiwigField';
import BoolField from '../HistoryFields/BoolField';
import {
  UPDATED_FIELD_ARRAY,
  UPDATED_FIELD_BOOL,
  UPDATED_FIELD_DATE,
  UPDATED_FIELD_RANGE,
  UPDATED_FIELD_TAG,
  UPDATED_FIELD_TEXT,
  UPDATED_FIELD_WYSIWYG,
} from '../../../../../constants/history';
import { getLocaleDateString } from '../../../../../helpers/date';

const CHARGE_GROUP_KEY = 'invoice_charges_updated';
const CHARGE_FIELD_KEY = 'invoice_charge_field_updated';
const CHARGE_REMOVED_KEY = 'invoice_charge_removed';
const CHARGE_TITLE_TRANSLATION_KEY = 'invoice_charge_title_updated';

const useStyles = makeStyles({
  childs: {
    margin: '0px',
  },
  child: {
    margin: '2px',
    marginLeft: '-20px',
  },
  chargeGroupLabel: {
    margin: '4px 0 2px',
    marginLeft: '-20px',
    listStyle: 'none',
  },
  chargeGroupLabelText: {
    fontSize: '13px',
    fontWeight: 600,
    color: 'black',
  },
  date: {
    marginLeft: '5px',
  },
});

const TYPED_FIELDS = [
  UPDATED_FIELD_TEXT,
  UPDATED_FIELD_RANGE,
  UPDATED_FIELD_BOOL,
  UPDATED_FIELD_WYSIWYG,
  UPDATED_FIELD_DATE,
  UPDATED_FIELD_TAG,
  UPDATED_FIELD_ARRAY,
];

// dispatch of different display of details (text, date, range, array, etc...)
const getChildUpdatedDetailsTranslation = (
  details: HistoryDetails
): JSX.Element => {
  const classes = useStyles();
  const t = useTranslate();

  const isTypedField = TYPED_FIELDS.includes(details.type as string);

  return (
    <>
      {details.type === UPDATED_FIELD_TEXT && <TextField details={details} />}
      {details.type === UPDATED_FIELD_RANGE && <RangeField details={details} />}
      {details.type === UPDATED_FIELD_BOOL && <BoolField details={details} />}
      {details.type === UPDATED_FIELD_WYSIWYG && (
        <WysiwygField details={details} />
      )}
      {details.type === UPDATED_FIELD_DATE && <DateField details={details} />}
      {details.type === UPDATED_FIELD_TAG && <TagField details={details} />}
      {details.type === UPDATED_FIELD_ARRAY && <ArrayField details={details} />}
      {/* Fallback for details with no recognized `type` (e.g. a plain
          message/technical_detail child, not a before/after field diff):
          translate the raw args directly, same as the parent-level details
          in HistoryItems/component.tsx's getDetailsTranslation. Without this,
          the child renders no text but its <li> bullet still shows, leaving
          an empty bullet under the parent's own message. */}
      {!isTypedField && details.translation_key && (
        <span>{t(`${details.translation_key}.details`, details.args)}</span>
      )}
      {details.args &&
        typeof details.args.date !== 'undefined' &&
        details.args.date && (
          <span className={classes.date}>
            {getLocaleDateString(details.args.date as string)}
          </span>
        )}
    </>
  );
};

const renderChildItem = (
  c: HistoryType,
  classes: ReturnType<typeof useStyles>
) => (
  <li className={classes.child} key={c.id}>
    <MuiTypography>
      {c.details && getChildUpdatedDetailsTranslation(c.details)}
      {!isEmpty(c.childs) && <HistoryItemsChildsUpdated childs={c.childs} />}
    </MuiTypography>
  </li>
);

// Recurcivity of display history childs updated
const HistoryItemsChildsUpdated: FunctionComponent<HistoryItemsChildsUpdatedProps> =
  (props) => {
    const classes = useStyles();
    const t = useTranslate();
    const { childs } = props;

    // Non-charge items: everything not related to charge lines
    const nonChargeChilds = childs.filter(
      (c) =>
        (c.key as string) !== CHARGE_GROUP_KEY &&
        (c.key as string) !== CHARGE_FIELD_KEY &&
        (c.key as string) !== CHARGE_REMOVED_KEY
    );

    // The group container (invoice_charges_updated)
    const chargeGroupItems = childs.filter(
      (c) => (c.key as string) === CHARGE_GROUP_KEY
    );

    // Individual field update events (invoice_charge_field_updated)
    const chargeFieldItems = childs.filter(
      (c) => (c.key as string) === CHARGE_FIELD_KEY
    );

    // invoice_charge_removed events
    const chargeRemovedItems = childs.filter(
      (c) => (c.key as string) === CHARGE_REMOVED_KEY
    );

    // Detect if all field updates represent a deletion (all after = empty)
    const isChargeLineDeletion =
      chargeFieldItems.length > 0 &&
      chargeFieldItems.every((c) => isEmpty(c.details?.args?.after));

    // Detect if all field updates represent an addition (all before = empty)
    const isChargeLineAddition =
      chargeFieldItems.length > 0 &&
      chargeFieldItems.every((c) => isEmpty(c.details?.args?.before));

    // Find the title field among field updates
    const chargeTitleItem = chargeFieldItems.find((c) =>
      c.details?.translation_key?.includes(CHARGE_TITLE_TRANSLATION_KEY)
    );

    const renderChargeFieldSimple = (c: HistoryType) => {
      const value = isChargeLineDeletion
        ? c.details?.args?.before
        : c.details?.args?.after;

      if (!value || !c.details?.translation_key) {
        return null;
      }

      return (
        <li className={classes.child} key={c.id}>
          <MuiTypography>
            {t(`${c.details.translation_key}.details_simple`, { value })}
          </MuiTypography>
        </li>
      );
    };

    const renderChargeFieldItems = () => {
      const deletionTitle = isChargeLineDeletion
        ? chargeTitleItem?.details?.args?.before || null
        : null;
      const additionTitle = isChargeLineAddition
        ? chargeTitleItem?.details?.args?.after || null
        : null;
      const updateTitle =
        !isChargeLineDeletion && !isChargeLineAddition
          ? chargeFieldItems[0]?.details?.args?.charge_title || null
          : null;

      return (
        <>
          {deletionTitle && (
            <li className={classes.chargeGroupLabel}>
              <MuiTypography className={classes.chargeGroupLabelText}>
                {t('history.invoices.invoice_charge_removed.header', {
                  title: deletionTitle,
                })}
              </MuiTypography>
            </li>
          )}
          {additionTitle && (
            <li className={classes.chargeGroupLabel}>
              <MuiTypography className={classes.chargeGroupLabelText}>
                {t('history.invoices.invoice_charge_added.header', {
                  title: additionTitle,
                })}
              </MuiTypography>
            </li>
          )}
          {updateTitle && (
            <li className={classes.chargeGroupLabel}>
              <MuiTypography className={classes.chargeGroupLabelText}>
                {t('history.invoices.invoice_charge_updated.header', {
                  title: updateTitle,
                })}
              </MuiTypography>
            </li>
          )}
          {isChargeLineDeletion || isChargeLineAddition
            ? chargeFieldItems.map((c) => renderChargeFieldSimple(c))
            : chargeFieldItems.map((c) => renderChildItem(c, classes))}
        </>
      );
    };

    return (
      <ul className={classes.childs}>
        {nonChargeChilds.map((c) => renderChildItem(c, classes))}
        {chargeGroupItems.length > 0 && (
          <li className={classes.chargeGroupLabel}>
            <MuiTypography className={classes.chargeGroupLabelText}>
              {t('history.invoices.invoice_charges_updated.title')}
            </MuiTypography>
          </li>
        )}
        {chargeGroupItems.map((c) => renderChildItem(c, classes))}
        {chargeFieldItems.length > 0 && renderChargeFieldItems()}
        {chargeRemovedItems.map((c) => {
          const title =
            c.details?.args?.before || c.details?.args?.title || null;

          return (
            <li className={classes.child} key={c.id}>
              <MuiTypography>
                {title
                  ? t('history.invoices.invoice_charge_removed.details', {
                      before: title,
                    })
                  : t(
                      'history.invoices.invoice_charge_removed.details_unknown'
                    )}
              </MuiTypography>
            </li>
          );
        })}
      </ul>
    );
  };

export default HistoryItemsChildsUpdated;
